import { useState } from "react";
import { Link } from "wouter";
import { Shell } from "@/components/layout/Shell";
import { COLOR_LIST, ColorFamily } from "@/data/colors";
import { useSeo } from "@/hooks/use-seo";
import { motion, AnimatePresence } from "framer-motion";

type Filter = "all" | ColorFamily;

const FILTERS: { id: Filter; label: string; description: string }[] = [
  { id: "all",     label: "All 175",  description: "Every color in the library" },
  { id: "warm",    label: "Warm",     description: "Reds, oranges & yellows — energising and action-oriented" },
  { id: "growth",  label: "Growth",   description: "Greens — life, calm, and natural vitality" },
  { id: "cool",    label: "Cool",     description: "Blues & blue-greens — trust, clarity, and focus" },
  { id: "mystic",  label: "Mystic",   description: "Violets & purples — creativity and depth" },
  { id: "neutral", label: "Neutral",  description: "Blacks, whites, grays & earthy tones — restraint and authority" },
];

export default function Learn() {
  const [active, setActive] = useState<Filter>("all");

  const filtered = active === "all"
    ? COLOR_LIST
    : COLOR_LIST.filter((c) => c.family === active);

  const activeInfo = FILTERS.find((f) => f.id === active)!;

  useSeo({
    title: "The Language of Color — 175 Colors Explained | Mychromamind",
    description:
      "Browse a library of 175 colors and what each one means in color psychology — pick a color to read its full personality and decision-making meaning.",
    path: "/learn",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "The Language of Color",
      description: "A library of 175 colors and their meanings in color psychology.",
    },
  });

  return (
    <Shell>
      <div className="max-w-5xl mx-auto px-6 py-12 md:py-24 w-full">

        {/* Header */}
        <div className="mb-10 md:mb-14 max-w-2xl">
          <h1 className="text-4xl md:text-5xl font-serif mb-6">The Language of Color</h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Color psychology is the study of how colors affect human behavior, mood, and
            physiological reactions. While personal associations vary, broad cultural and
            biological patterns give each hue a distinct emotional frequency. Choose a
            color to read its full meaning.
          </p>
        </div>

        {/* Family filter tabs */}
        <div className="mb-8">
          <div
            role="tablist"
            aria-label="Filter by color family"
            className="flex flex-wrap gap-2"
          >
            {FILTERS.map((f) => (
              <button
                key={f.id}
                role="tab"
                aria-selected={active === f.id}
                onClick={() => setActive(f.id)}
                className={[
                  "px-4 py-1.5 rounded-full text-sm font-medium transition-colors border",
                  active === f.id
                    ? "bg-foreground text-background border-foreground"
                    : "bg-transparent text-muted-foreground border-border hover:border-foreground/40 hover:text-foreground",
                ].join(" ")}
              >
                {f.label}
              </button>
            ))}
          </div>
          {active !== "all" && (
            <p className="mt-3 text-sm text-muted-foreground">
              {activeInfo.description} — {filtered.length} colors
            </p>
          )}
        </div>

        {/* Color grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6 md:gap-8">
          <AnimatePresence mode="popLayout">
            {filtered.map((color, idx) => (
              <motion.div
                key={color.id}
                layout
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.92 }}
                transition={{ delay: Math.min(idx * 0.012, 0.4), duration: 0.35 }}
              >
                <Link
                  href={`/learn/${color.id}`}
                  className="group flex flex-col items-center gap-3 text-center focus:outline-none"
                  aria-label={`Read about ${color.name}`}
                >
                  <span
                    className="w-full aspect-square rounded-2xl shadow-sm border border-black/5 dark:border-white/5 transition-transform duration-300 group-hover:scale-105 group-focus-visible:ring-2 group-focus-visible:ring-primary"
                    style={{ backgroundColor: color.hex }}
                  />
                  <span className="text-sm md:text-base font-serif group-hover:text-primary transition-colors">
                    {color.name}
                  </span>
                </Link>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

      </div>
    </Shell>
  );
}
